import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "s-paginated-table"
}
const _hoisted_2 = { class: "s-paginated-table-loading__mask" }
const _hoisted_3 = { class: "s-paginated-table-pagination__wrapper" }
const _hoisted_4 = { class: "s-paginated-table-pagination__wrapper_text" }
const _hoisted_5 = /*#__PURE__*/_createTextVNode(" Refresh table data ")
const _hoisted_6 = { class: "s-paginated-table-pagination__wrapper_right" }
const _hoisted_7 = { key: 1 }

import {
  computed,
  PropType,
  Ref,
  reactive,
  ref,
  UnwrapRef,
  watch,
  watchEffect,
} from 'vue';
import { CircularQuarterRing } from '@simmons/components/icons/spinners';
import {
  FetchOptions,
  PaginationOptions,
  SortObject,
  AsyncGqlTableColumn,
  AsyncTableRefetch,
  AsyncTableFetchError,
  AsyncTableFilters,
} from './AsyncGqlTable.types';
import {
  STable,
  SPagination,
  SLoader,
  SButton,
} from '@simmons/components';
import { SortObj } from '@simmons/component-library/src/components/s-table/STable.types';

import { useQuery } from '@vue/apollo-composable';
import useSnackbar from '@/composables/useSnackbar';
import { isEqual } from 'lodash-es';
import useRouteQuery from '@/utils/useRouteQuery';

type PaginationOptionsType = UnwrapRef<typeof paginationOptions>;


export default /*#__PURE__*/_defineComponent({
  props: {
  tableClass: {
    type: String,
    required: false,
    default: null,
  },
  query: {
    type: Object as PropType<Record<string, any>>,
    required: true,
  },
  queryWhere: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  countQuery: {
    type: Object,
    required: true,
  },
  responseMapper: {
    type: Function,
    required: true,
  },
  countResponseMapper: {
    type: Function,
    required: true,
  },
  columns: {
    type: Array as PropType<AsyncGqlTableColumn[]>,
    required: true,
  },
  filters: {
    type: Array as PropType<AsyncTableFilters>,
    required: false,
    default: () => [],
  },
  defaultSort: {
    type: Object as PropType<SortObj>,
    default: undefined,
  },
  handleSort: {
    type: Function as PropType<(sortObj: SortObject) => string>,
    required: false,
    default: undefined,
  },
  stickyHeader: {
    type: Boolean,
    required: false,
    default: false,
  },
},
  emits: ["get:tableData", "get:tableDataRefetch", "error:fetch-data", "error:fetch-total", "sort"],
  setup(__props, { emit }: { emit: ({(event: 'get:tableData', tableData: Ref<unknown>): void;
  (event: 'get:tableDataRefetch', tableDataRefetch: AsyncTableRefetch): void;
  (event: 'error:fetch-data', error: AsyncTableFetchError): void;
  (event: 'error:fetch-total', error: AsyncTableFetchError): void;
  (event: 'sort', sortBy: string | undefined): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props



const policy = {
  errorPolicy: 'all',
  fetchPolicy: 'cache-and-network',
};


const snackbar = useSnackbar();

const availableItemsPerPageOptions = [5, 10, 25, 50, 100];
const isTableLoading = ref(false);

const resolveSortParam = (sortObj?: SortObject): string | undefined => {
  if (!sortObj) {
    return undefined;
  }

  const { key, isAscending } = sortObj;

  if (props.handleSort) {
    return props.handleSort(sortObj);
  }

  return isAscending ? `${key}_ASC` : `${key}_DESC`;
};

const paginationOptions = reactive<PaginationOptions>({
  itemsPerPage: 10,
  currentPage: 1,
  orderBy: resolveSortParam(props.defaultSort),
});

watchEffect(() => {
  emit('sort', paginationOptions.orderBy);
});

const { update, get } = useRouteQuery<PaginationOptionsType>();

const where = computed<FetchOptions['where']>(() => props.queryWhere);

const countQuery = computed<FetchOptions>(() => ({
  ...where.value,
}));

const queryOptions = computed<FetchOptions>(() => ({
  first: paginationOptions.itemsPerPage,
  skip: (paginationOptions.currentPage - 1) * paginationOptions.itemsPerPage,
  orderBy: paginationOptions.orderBy,
  ...countQuery.value,
}));

const {
  result: dataResult,
  loading: dataLoading,
  error: dataError,
  refetch: dataRefetch,
  onError: onDataError,
} = useQuery(
  props.query,
  queryOptions,
  policy,
);

const {
  result: totalResult,
  loading: totalLoading,
  error: totalError,
  refetch: totalRefetch,
  onError: onTotalError,
} = useQuery(
  props.countQuery,
  countQuery,
  policy,
);

const tableData = computed(() => (props.responseMapper(dataResult.value)));
const totalItems = computed(() => (props.countResponseMapper(totalResult.value)));

watch(dataLoading, (isLoading: boolean) => {
  if (!isLoading) {
    emit('get:tableData', tableData);
    emit('get:tableDataRefetch', dataRefetch);
  }
});

const paginationText = computed(() => {
  const skip = queryOptions.value.skip || 0;
  const fromValue = (skip + 1) < totalItems.value
    ? skip + 1
    : totalItems.value;
  const toValue = (skip + tableData.value.length) < totalItems.value
    ? skip + tableData.value.length
    : totalItems.value;

  return `Showing ${tableData.value.length ? fromValue : 0} to ${toValue} of ${totalItems.value} entries`;
});

const handlePageChange = (page: number): void => {
  paginationOptions.currentPage = page;
};

const resetPage = () => {
  handlePageChange(1);
};

const handleSortChange = (sortObj: SortObject): void => {
  paginationOptions.orderBy = resolveSortParam(sortObj);
  resetPage();
};

const reloadTableData = async () => {
  isTableLoading.value = true;
  await dataRefetch();
  await totalRefetch();
  isTableLoading.value = false;
};

watch(queryOptions, async (newOptions, oldOptions) => {
  const whereWasUpdated = !isEqual(newOptions.where, oldOptions.where);

  if (whereWasUpdated) {
    resetPage();
  } else {
    await update({
      ...paginationOptions,
    });
  }
});

onDataError((error) => {
  snackbar.danger({ messageContent: `Data is not loaded, ${error}` });
  console.error(error);
  emit('error:fetch-data', error);
});

onTotalError((error) => {
  snackbar.danger({ messageContent: `Total value is not loaded, ${error}` });
  console.error(error);
  emit('error:fetch-total', error);
});

const applyRouteQuery = () => {
  const routeQuery = get();

  paginationOptions.itemsPerPage = routeQuery.itemsPerPage || 10;
  paginationOptions.currentPage = routeQuery.currentPage || 1;
  paginationOptions.orderBy = routeQuery.orderBy || resolveSortParam(props.defaultSort);
};

applyRouteQuery();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (!_unref(dataError) && !_unref(totalError))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", {
            class: _normalizeClass(["s-paginated-table-loading", {
          active: _unref(dataLoading) || _unref(totalLoading) || isTableLoading.value,
        }])
          }, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(SLoader), {
                loading: _unref(dataLoading) || _unref(totalLoading) || isTableLoading.value,
                height: 100,
                width: 100,
                "spinner-icon": _unref(CircularQuarterRing),
                position: "centre"
              }, null, 8, ["loading", "spinner-icon"])
            ]),
            _createVNode(_unref(STable), {
              class: _normalizeClass(__props.tableClass),
              columns: __props.columns,
              data: _unref(tableData),
              "sticky-header": props.stickyHeader,
              "default-sort": __props.defaultSort,
              onOnSort: handleSortChange
            }, _createSlots({ _: 2 }, [
              _renderList(__props.filters, (filter) => {
                return {
                  name: `${filter.name}_header_append`,
                  fn: _withCtx(({ itemData, rowData }) => [
                    _renderSlot(_ctx.$slots, `${filter.name}_header_append`, {
                      itemData: itemData,
                      rowData: rowData
                    })
                  ])
                }
              }),
              _renderList(__props.columns, (column) => {
                return {
                  name: `data-${column.name}`,
                  fn: _withCtx(({ itemData, rowData }) => [
                    _renderSlot(_ctx.$slots, `data-${column.name}`, {
                      itemData: itemData,
                      rowData: rowData
                    })
                  ])
                }
              })
            ]), 1032, ["class", "columns", "data", "sticky-header", "default-sort"])
          ], 2),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(paginationText)), 1),
            _createVNode(_unref(SButton), {
              type: "tertiary",
              class: "refresh",
              onClick: reloadTableData
            }, {
              default: _withCtx(() => [
                _hoisted_5
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_unref(SPagination), {
                total: _unref(totalItems),
                "per-page": _unref(paginationOptions).itemsPerPage,
                "current-page": _unref(paginationOptions).currentPage,
                "max-visible-buttons": 3,
                onPagechanged: handlePageChange
              }, null, 8, ["total", "per-page", "current-page"]),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(paginationOptions).itemsPerPage) = $event)),
                class: "s-select"
              }, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(availableItemsPerPageOptions, (option) => {
                  return _createElementVNode("option", { key: option }, _toDisplayString(option), 1)
                }), 64))
              ], 512), [
                [
                  _vModelSelect,
                  _unref(paginationOptions).itemsPerPage,
                  void 0,
                  { number: true }
                ]
              ])
            ])
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, " Something went wrong during fetching the table data! "))
  ]))
}
}

})